import WRAP from "WRAP-JS";
import { configCycloneDhWniIndex, configCycloneWniLayer } from "./config/ConfigTropicalCyclone";
import { formatLonLatDM, formatUTCDate, parseUTCDate } from "../../../utils/FPEUtils";
import LegendTropicalCyclone from "../legend/LegendTropicalCyclone";
import { WX_ID_TROPICALCYCLONE } from "../../../store/WeatherContentsState";

class TropicalCyclone {

  static NAME = WX_ID_TROPICALCYCLONE;

  dh;
  layer;

  ready = false;
  visible;
  dispatcher;
  issueTime = '---';

  constructor() {
    this.layer = new WRAP.Geo.Layer('TROPICAL_CYCLONE');
    this.layer.setVisible(false);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dh = new WRAP.Data(configCycloneDhWniIndex);
      this.dh.updated('index', ref => {
        this._selectBaseValidTime(ref);
        resolve(ref);
      });
    });
  }

  initLayer() {
    this.layer.configure(this.dh, configCycloneWniLayer);
    this.layer.setTooltip(this._tooltip);
  }

  initInteraction() {
    WRAP.Geo.addEventHandler('mouseover', (layer, feature, sp) => {
      if (layer === this.layer) {
        if (feature.geo) {
          const conf = configCycloneWniLayer.Attributes;
          const sameFeatures = layer._dl.filter(f => f.geo === feature.geo);
          sameFeatures.forEach(f => {
            const style = f.style();
            style.lineWidth = conf.wind_highlight_line_width || style.lineWidth;
          });
          WRAP.Geo.invalidate();
        }
      }
    });

    WRAP.Geo.addEventHandler('mouseout', (layer, feature, sp) => {
      if (layer === this.layer) {
        if (feature.geo) {
          const conf = configCycloneWniLayer.Attributes;
          const sameFeatures = layer._dl.filter(f => f.geo === feature.geo);
          sameFeatures.forEach(f => {
            const style = f.style();
            style.lineWidth = conf.wind_line_width || style.lineWidth;
          });
          WRAP.Geo.invalidate();
        }
      }
    });
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this.layer;
  }

  setVisible(visible) {
    if (!this.ready) return;

    if (visible) {
      this.layer.show();
    } else {
      this.layer.hide();
    }

    this.dh.setAutoUpdate('index', visible);

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});

  }

  getTimeInformation() {
    const issueTime = this.issueTime !== '---' ? formatUTCDate(parseUTCDate(this.issueTime), '%Y/%m/%d %H:%MZ') : this.issueTime;
    return {
      id: TropicalCyclone.NAME,
      label: 'TropicalCyclone',
      baseTime: issueTime
    }
  }

  getLegend() {
    return {
      id: TropicalCyclone.NAME,
      label: 'T Cyclone',
      component: LegendTropicalCyclone,
      contents: this
    };
  }

  changeRoutePointEditing(routePointEditing) {
    this.layer.disableInteraction(routePointEditing);
  }

  _selectBaseValidTime(ref) {
    let timeList = ref;
    if (!timeList) return;
    timeList = timeList.index;

    const maxIssueTime = Object.values(timeList).reduce((maxIssue, tc) => {
      return maxIssue < tc['issue_date'] ? tc['issue_date'] : maxIssue
    }, this.issueTime);

    if (this.issueTime !== maxIssueTime) {
      this.issueTime = maxIssueTime;
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
    }
  }

  _tooltip(geo) {
    if (!geo) return;

    const p = geo.properties;

    if (!p) return;

    const name = p.ename && p.ename !== '_missing_' ? p.ename : '---';
    const type = p.wrap_feature_type === 'forecast' ? 'Forecast' : 'Analysis';
    const valid = p.valid ? formatUTCDate(parseUTCDate(p.valid), '%d / %HZ') : '---';
    const position = formatLonLatDM(geo.geometry.coordinates[0], geo.geometry.coordinates[1]);
    const pressure = p.pressure && p.pressure !== '_missing_' ? `${p.pressure}hPa` : '---';
    const dir = p.heading && p.heading !== '_missing_' ? `${p.heading}&deg;` : '---';
    const speed = p.speed && p.speed !== '_missing_' ? `${p.speed}KT` : '---';
    const maxSpeed = p.max_wind_speed && p.max_wind_speed !== '_missing_' ? `${p.max_wind_speed}KT` : '---';
    const gust = p.gusts && p.gusts !== '_missing_' ? `${p.gusts}KT` : '---';
    const maxGust = `${maxSpeed} / ${gust}`;

    let max35 = [p.stmrds_gale_1, p.stmrds_gale_2, p.stmrds_gale_3, p.stmrds_gale_4].reduce((maxGale, gale) => {
      return maxGale < gale ? gale : maxGale;
    }, -1);

    let max50 = [p.stmrds_storm_1, p.stmrds_storm_2, p.stmrds_storm_3, p.stmrds_storm_4].reduce((maxStorm, storm) => {
      return maxStorm < storm ? storm : maxStorm;
    }, -1);

    const text = [];
    text.push(`<ul style="text-align: left;">
      <li>${name}</li>
      <li>${type} : ${valid}</li>
      <li>Position : ${position}</li>
      <li>Pressure : ${pressure}</li>
      <li>Dir : ${dir}</li>
      <li>Speed : ${speed}</li>
      <li>Max / Gust : ${maxGust}</li>`
    );

    if (max35 !== -1) {
      text.push(`<li>35KT Max Radius : ${max35}NM</li>`);
    }

    if (max50 !== -1) {
      text.push(`<li>50KT Max Radius : ${max50}NM</li>`);
    }

    text.push('</ul>');
    return text.join('');
  }
}

export default TropicalCyclone;